import {Link} from 'react-router-dom';
// import {generateCreatePath, generateEditPath, generateShowPath} from './ra-helpers';
// import {useDelete, useResourceContext, useSaveContext, useUpdate} from 'ra-core';
import {PencilIcon, EyeIcon, RefreshCwIcon, Folder} from 'lucide-react';
import {useFormContext} from 'react-hook-form';
import useDialogState from 'hooks/useDialogState';
import {Button, ButtonProps} from '@/shadcn_components/ui/button';
import {
  CanAccess,
  useCan,
  UseCanProps,
  useCreateButton,
  useEditButton,
  useInvalidate,
  UseInvalidateProp,
  useResourceParams,
  useShowButton,
} from '@refinedev/core';
import {generateCreatePath, generateEditPath, generateShowPath} from './helpers';
import {BaseID} from '@/hooks/types';
import {DeleteIconButton} from '../basic/buttons';
import {DeleteConfirmDialog} from './dialogs/delete-confirm-dialog';

export type CreateButtonProps = Parameters<typeof useCreateButton>[0] & {
  resource?: string;
  defaultValues?: any;
  // forceCan?: boolean;
  canParams?: UseCanProps['params'];
};

export type EditButtonProps = Parameters<typeof useEditButton>[0] & {
  id: string;
  resource?: string;
  // forceCan?: boolean;
  canParams?: UseCanProps['params'];
};

export type ShowButtonProps = Parameters<typeof useShowButton>[0] & {
  id: string;
  resource?: string;
  // forceCan?: boolean;
  // my_role?: string; //my role with this resource
  canParams?: UseCanProps['params'];
};

// export type DetachButtonProps = Omit<ButtonProps, 'ref' | 'children'> & {
//   resource?: string;
//   id?: string;
//   relation: string;
//   relation_id: string;
// };

export type RefreshButtonProps = Omit<ButtonProps, 'ref' | 'children'> & {
  resource?: string;
  id?: string;
} & Partial<Pick<UseInvalidateProp, 'invalidates'>>;

export const RefreshButton = (props: RefreshButtonProps) => {
  const {invalidates = ['all'], resource, id} = props;
  // const {resource, id} = useResourceParams(props);
  const invalidate = useInvalidate();

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-8 lg:flex"
      // {...props}
      onClick={() => {
        invalidate({id, invalidates, resource});
      }}>
      <RefreshCwIcon className="w-4 h-4" />
    </Button>
  );
};

export const CreateButton = (props: CreateButtonProps) => {
  const {resource} = useResourceParams(props);
  const {defaultValues, ...restOfProps} = props;
  if (!resource?.name) throw 'Resource must be specified for Create button';
  // const {forceCan} = props;
  const {data: canAccess} = useCan({
    resource: resource.name,
    action: 'create',
    params: {...props.canParams},
    queryOptions: {
      // enabled: props.forceCan === undefined,
    },
  });
  if (canAccess?.can === false) return null;
  // if ((canAccess?.can === false && forceCan === undefined) || forceCan === false) return null;

  return (
    <Link to={generateCreatePath(resource.name, defaultValues)}>
      <Button
        size="sm"
        // {...restOfProps}
      >
        Tạo mới
      </Button>
    </Link>
  );
};

export const EditButton = (props: EditButtonProps) => {
  const {resource, id} = useResourceParams(props);
  if (!resource?.name || !id) throw 'Resource and id must be specified for Edit button';
  // const {forceCan} = props;

  const {data: canAccess} = useCan({
    resource: resource.name,
    action: 'update',
    params: {...props.canParams, id: id},
    queryOptions: {
      // enabled: props.forceCan === undefined,
    },
  });
  if (canAccess?.can === false) return null;

  // if ((canAccess?.can === false && forceCan === undefined) || forceCan === false) return null;

  return (
    <Link to={generateEditPath(resource?.name, String(id))}>
      <Button
        variant="ghost"
        size="icon"
        className="px-1 py-1 rounded-md text-blue-600 hover:bg-blue-50 hover:border-blue-600 hover:border hover:text-blue-600"
        title="Chỉnh sửa"
        // {...props}
      >
        <PencilIcon className="w-4 h-4" />
      </Button>
    </Link>
  );
};

export const ShowButton = (props: ShowButtonProps) => {
  const {resource, id} = useResourceParams(props);
  if (!resource?.name || !id) throw 'Resource and id must be specified for Show button';
  // const {forceCan, ...rest} = props;

  const {data: canAccess} = useCan({
    resource: resource.name,
    action: 'read',
    params: {...props.canParams, id: id},
    queryOptions: {
      // enabled: forceCan === undefined,
    },
  });
  // console.log('canAccess ', id, props.canParams, canAccess);
  if (canAccess?.can === false) return null;

  // if ((canAccess?.can === false && forceCan === undefined) || forceCan === false) return null;

  return (
    <Link to={generateShowPath(resource?.name, String(id))}>
      <Button
        variant="ghost"
        size="icon"
        className="px-1 py-1 rounded-md text-green-600 hover:bg-green-50 hover:border-green-600 hover:border hover:text-green-600"
        title="Xem chi tiết">
        <EyeIcon className="w-4 h-4" />
      </Button>
    </Link>
  );
};

export const DocumentButton = ({
  id,
  // forceCan,
  resource = 'projects',
  ...props
}: {
  resource?: string;
  id: string;
  canParams?: UseCanProps['params'];
  // forceCan?: boolean;
}) => {
  if (!id) throw 'ID must be specified for Document button';

  const {data: canAccess} = useCan({
    resource: resource,
    action: 'read',
    params: {...props.canParams, id: id},
    queryOptions: {
      // enabled: forceCan === undefined,
    },
  });
  if (canAccess?.can === false) return null;
  // if ((canAccess?.can === false && forceCan === undefined) || forceCan === false) return null;

  return (
    <Link to={`${generateShowPath(resource, String(id))}?tab=documents`}>
      <Button
        variant="ghost"
        size="icon"
        title="Tài liệu"
        className="px-1 py-1 rounded-md text-blue-600 hover:bg-blue-50 hover:border-blue-600 hover:border hover:text-blue-600"
        // {...props}
      >
        <Folder className="w-4 h-4" />
      </Button>
    </Link>
  );
};

export type DeleteButtonProps = Omit<ButtonProps, 'ref' | 'children'> & {
  id: BaseID;
  resource: string;
  title?: string;
  content?: string;
  canParams?: UseCanProps['params'];
  onSuccess?: () => void;
};

export const DeleteButtonWithConfirmation = (props: DeleteButtonProps) => {
  const {resource, id} = useResourceParams(props);
  if (!resource?.name || !id) throw 'Resource and id must be specified for Edit button';
  // const {forceCan} = props;
  const dialogState = useDialogState();

  const {data: canAccess} = useCan({
    resource: resource.name,
    action: 'delete',
    params: {...props.canParams, id: id},
    queryOptions: {
      // enabled: forceCan === undefined,
    },
  });

  if (canAccess?.can === false) return null;

  return (
    <>
      <DeleteIconButton
        title="Xóa"
        onClick={e => {
          e.stopPropagation();
          // e.nativeEvent.stopPropagation();
          dialogState.openDialog({resource: resource.name, id: id});
        }}
        // {...props}
      />
      <DeleteConfirmDialog
        dialogState={dialogState}
        title={props.title}
        content={props.content}
        onSuccess={props.onSuccess}
      />
    </>
  );
};

export const SaveButton = (props: Omit<ButtonProps, 'ref' | 'children'> & {label?: string}) => {
  const {
    formState: {isSubmitting},
  } = useFormContext();
  return (
    <Button variant="default" size="sm" type="submit" {...props} disabled={isSubmitting}>
      {props.label || 'Lưu'}
    </Button>
  );
};
